import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import { faArrowLeft, faFileAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface UploadProps {
  entityType: string;
  entityId: string;
  tenantId: string;
  docType?: string;
  purpose?: string;
  onUploadSuccess?: () => void;
  allowMultiple?: boolean;
  bucketName?: string;
}

interface UploadedDocument {
  id: string;
  fileName: string;
  s3Url: string;
  fileType: string;
}

const GenericSupportingDocs: React.FC<UploadProps> = ({
  entityType,
  entityId,
  tenantId,
  docType = 'SUPPORT_DOC',
  purpose = '',
  onUploadSuccess,
  allowMultiple = true,
  bucketName = 'default-docs-bucket',
}) => {
  const [files, setFiles] = useState<FileList | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [uploadedDocs, setUploadedDocs] = useState<UploadedDocument[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const isImage = (type: string) => type.startsWith('image/');

  const fetchUploadedDocs = async () => {
    try {
      const res = await axios.get(`/api/documents/${tenantId}/${entityType}/${entityId}`);
      setUploadedDocs(res.data || []);
    } catch (err) {
      console.error('Failed to fetch uploaded documents', err);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFiles(e.target.files);
    setError('');
  };

  const resetUpload = () => {
    setFiles(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setError('');
  };

  const handleUpload = async () => {
    if (!files || files.length === 0) {
      setError('Please select at least one file to upload.');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const uploads = Array.from(files).map(async (file) => {
        const formData = new FormData();
        const fileId = uuidv4();

        formData.append('file', file);
        formData.append('tenant_id', tenantId);
        formData.append('entity_type', entityType);
        formData.append('entity_id', entityId);
        formData.append('doc_type', docType);
        formData.append('purpose', purpose);
        formData.append('bucket_name', bucketName);
        formData.append('file_name', file.name);
        formData.append('file_type', file.type);
        formData.append('file_extension', file.name.split('.').pop() || '');
        formData.append('size', file.size.toString());

        await axios.post('/api/documents/upload', formData);
      });

      await Promise.all(uploads);

      resetUpload();
      if (onUploadSuccess) onUploadSuccess();
      fetchUploadedDocs();
    } catch (err) {
      console.error(err);
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    fetchUploadedDocs();
  }, []);

  return (
    <div className="p-6 bg-white rounded shadow space-y-6">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center text-sm text-blue-600 hover:underline"
      >
        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
        Back
      </button>

      {/* Upload Section */}
      <div>
        <h2 className="text-xl font-semibold mb-3">Upload Supporting Documents</h2>

        <div className="space-y-3">
          <input
            ref={fileInputRef}
            type="file"
            multiple={allowMultiple}
            onChange={handleFileChange}
            className="block w-full border rounded px-3 py-2"
            aria-label="Select files to upload"
          />

          {files && (
            <div className="text-sm text-gray-600">
              {files.length} file{files.length > 1 ? 's' : ''} selected.
            </div>
          )}

          {error && <div className="text-red-600 text-sm">{error}</div>}

          <div className="flex gap-2">
            <button
              onClick={handleUpload}
              disabled={uploading || !files}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
            <button
              onClick={resetUpload}
              className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Uploaded Files */}
      <div>
        <h3 className="text-md font-semibold mb-2">Uploaded Files</h3>

        {uploadedDocs.length === 0 ? (
          <div className="text-gray-500 text-sm">No files uploaded yet.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {uploadedDocs.map((doc) => (
              <div
                key={doc.id}
                className="border rounded p-3 shadow-sm bg-gray-50 transition hover:shadow-md"
              >
                {isImage(doc.fileType) ? (
                  <img
                    src={doc.s3Url}
                    alt={doc.fileName}
                    className="w-full h-32 object-cover rounded mb-2 border"
                  />
                ) : (
                  <div className="text-gray-700 text-sm mb-2 flex items-center">
                    <FontAwesomeIcon icon={faFileAlt} className="mr-2 text-lg text-gray-500" />
                    {doc.fileName}
                  </div>
                )}
                <a
                  href={doc.s3Url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 text-xs hover:underline"
                >
                  View / Download
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GenericSupportingDocs;